import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"
import { useIntl } from "react-intl"
import { Button } from "@/components/ui/button"
import { Select } from "@/components/ui/select"
import type { ContentAreaGroup, Flashcard } from "@/types/flashcards"

type CardFormProps = {
  idPrefix: string
  action: string
  method: "post" | "patch"
  contentAreaGroups: ContentAreaGroup[]
  card?: Flashcard
  submitLabel: string
  onSuccess?: () => void
  onCancel?: () => void
  autoFocus?: boolean
}

export function CardForm({
  idPrefix,
  action,
  method,
  contentAreaGroups,
  card,
  submitLabel,
  onSuccess,
  onCancel,
  autoFocus = false,
}: CardFormProps) {
  const intl = useIntl()
  const form = useForm({
    front: card?.front ?? "",
    back: card?.back ?? "",
    content_area_id: card?.content_area_id ? String(card.content_area_id) : "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.submit(method, action, {
      preserveScroll: true,
      onSuccess: () => {
        if (method === "post") form.reset("front", "back")
        onSuccess?.()
      },
    })
  }

  const hasAreas = contentAreaGroups.some((group) => group.areas.length > 0)

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor={`${idPrefix}-front`}>{intl.formatMessage({ id: "flashcards.front" })}</label>
        <textarea
          id={`${idPrefix}-front`}
          className="form-control form-control-textarea"
          required
          autoFocus={autoFocus}
          placeholder={intl.formatMessage({ id: "flashcards.front_placeholder" })}
          aria-invalid={!!form.errors.front}
          value={form.data.front}
          onChange={(e) => form.setData("front", e.target.value)}
        />
        {form.errors.front && <p className="text-xs text-danger-display">{form.errors.front}</p>}
      </div>
      <div className="space-y-2">
        <label htmlFor={`${idPrefix}-back`}>{intl.formatMessage({ id: "flashcards.back_side" })}</label>
        <textarea
          id={`${idPrefix}-back`}
          className="form-control form-control-textarea"
          required
          placeholder={intl.formatMessage({ id: "flashcards.back_placeholder" })}
          aria-invalid={!!form.errors.back}
          value={form.data.back}
          onChange={(e) => form.setData("back", e.target.value)}
        />
        {form.errors.back && <p className="text-xs text-danger-display">{form.errors.back}</p>}
      </div>
      {hasAreas && (
        <div className="space-y-2">
          <label htmlFor={`${idPrefix}-area`}>{intl.formatMessage({ id: "flashcards.content_area" })}</label>
          <Select
            id={`${idPrefix}-area`}
            value={form.data.content_area_id}
            onChange={(e) => form.setData("content_area_id", e.target.value)}
          >
            <option value="">{intl.formatMessage({ id: "flashcards.none" })}</option>
            {contentAreaGroups.length === 1
              ? contentAreaOptions(contentAreaGroups[0])
              : contentAreaGroups.map((group) => (
                  <optgroup key={group.label} label={group.label}>
                    {contentAreaOptions(group)}
                  </optgroup>
                ))}
          </Select>
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={form.processing}>
          {submitLabel}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel}>
            {intl.formatMessage({ id: "common.cancel" })}
          </Button>
        )}
      </div>
    </form>
  )
}

function contentAreaOptions(group: ContentAreaGroup) {
  return group.areas.map((area) => (
    <option key={area.id} value={area.id}>
      {area.name}
    </option>
  ))
}
